/**
 * RaiseArtefactModal
 *
 * Opens from a control's table row or detail page to quickly create a new
 * Issue, Incident, or Remediation pre-linked to that control.
 *
 * On submit it navigates to the /new page for that artefact type and
 * passes the control ID + register as URL params so RegisterItemDetail
 * can auto-link the item after it is saved.
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { issueRegistersApi, incidentRegistersApi, remediationRegistersApi } from '../services/api';

type ArtefactType = 'issue' | 'incident' | 'remediation';

interface Props {
  controlId?: string;
  controlTitle?: string;
  controlRegister?: string; // matrixId (for controls) or registerId (for risks)
  riskId?: string;
  riskTitle?: string;
  riskRegister?: string;
  allowedTypes?: ArtefactType[];
  onClose: () => void;
}

const TYPE_CONFIG: Record<
  ArtefactType,
  { label: string; icon: string; color: string; path: string }
> = {
  issue: {
    label: 'Issue',
    icon: 'ri-error-warning-line',
    color: 'warning',
    path: 'issues',
  },
  incident: {
    label: 'Incident',
    icon: 'ri-alarm-warning-line',
    color: 'danger',
    path: 'incidents',
  },
  remediation: {
    label: 'Remediation',
    icon: 'ri-tools-line',
    color: 'info',
    path: 'remediations',
  },
};

export const RaiseArtefactModal: React.FC<Props> = ({
  controlId,
  controlTitle,
  controlRegister,
  riskId,
  riskTitle,
  riskRegister,
  allowedTypes = ['issue', 'incident', 'remediation'],
  onClose,
}) => {
  const [selectedType, setSelectedType] = useState<ArtefactType>('issue');
  const [selectedRegister, setSelectedRegister] = useState('');

  const { data: issueRegisters = [] } = useQuery<any[]>({
    queryKey: ['issueRegisters'],
    queryFn: issueRegistersApi.getAll,
  });

  const { data: incidentRegisters = [] } = useQuery<any[]>({
    queryKey: ['incidentRegisters'],
    queryFn: incidentRegistersApi.getAll,
  });

  const { data: remediationRegisters = [] } = useQuery<any[]>({
    queryKey: ['remediationRegisters'],
    queryFn: remediationRegistersApi.getAll,
  });

  const registersMap: Record<ArtefactType, any[]> = {
    issue: issueRegisters,
    incident: incidentRegisters,
    remediation: remediationRegisters,
  };

  const currentRegisters = registersMap[selectedType];

  const handleTypeChange = (type: ArtefactType) => {
    setSelectedType(type);
    setSelectedRegister('');
  };

  const handleCreate = () => {
    if (!selectedRegister) return;
    const { path } = TYPE_CONFIG[selectedType];
    const params = new URLSearchParams({ registerId: selectedRegister });

    if (controlId) {
      params.set('autoLinkControlId', controlId);
      if (controlRegister) params.set('autoLinkControlRegister', controlRegister);
      if (controlTitle) params.set('autoLinkControlTitle', controlTitle);
    }
    if (riskId) {
      params.set('autoLinkRiskId', riskId);
      if (riskRegister) params.set('autoLinkRiskRegister', riskRegister);
      if (riskTitle) params.set('autoLinkRiskTitle', riskTitle);
    }

    window.location.href = `/${path}/new?${params.toString()}`;
    onClose();
  };

  const cfg = TYPE_CONFIG[selectedType];

  return createPortal(
    <>
      <div
        className="modal fade show d-block"
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        style={{ zIndex: 1090 }}
      >
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <div>
                <h5 className="modal-title">
                  <i className="ri-add-circle-line me-2"></i>
                  Raise Against {controlId ? 'Control' : 'Risk'}
                </h5>
                {(controlId || riskId) && (
                  <div className="np-sub mt-1">
                    <span className="np-chip np-tone-neutral font-monospace me-1">
                      {controlId || riskId}
                    </span>
                    {controlTitle || riskTitle}
                  </div>
                )}
              </div>
              <button type="button" className="btn-close" onClick={onClose}></button>
            </div>

            <div className="modal-body">
              {/* Type selector */}
              <div className="np-field">
                <label>Type</label>
                <div className="np-pill-toggle-group">
                  {(Object.keys(TYPE_CONFIG) as ArtefactType[]).filter((t) => allowedTypes.includes(t)).map((type) => {
                    const c = TYPE_CONFIG[type];
                    const isActive = selectedType === type;
                    return (
                      <button
                        key={type}
                        type="button"
                        className={`np-pill-toggle flex-fill ${isActive ? `active np-tone-${c.color}` : ''}`}
                        onClick={() => handleTypeChange(type)}
                      >
                        <i className={`${c.icon} me-1`}></i>
                        {c.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Register picker */}
              <div className="np-field np-mb-0">
                <label>
                  Register <span className="text-danger">*</span>
                </label>
                {currentRegisters.length === 0 ? (
                  <div className="np-banner np-tone-neutral mb-0">
                    <i className="ri-information-line"></i>
                    <div>
                      No {cfg.label} registers found. Create one in{' '}
                      <strong>Settings → {cfg.label} Registers</strong> first.
                    </div>
                  </div>
                ) : (
                  <select
                    className="np-select"
                    value={selectedRegister}
                    onChange={(e) => setSelectedRegister(e.target.value)}
                  >
                    <option value="">— Select a register —</option>
                    {currentRegisters.map((r: any) => (
                      <option key={r.registerId} value={r.registerId}>
                        {r.name || r.registerId}
                      </option>
                    ))}
                  </select>
                )}
                <small className="np-hint d-block">
                  The {cfg.label} will be created in this register and automatically linked to the control.
                </small>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="np-btn np-btn-ghost" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className={`np-btn np-btn-${cfg.color}`}
                onClick={handleCreate}
                disabled={!selectedRegister}
              >
                <i className="ri-add-line me-1"></i>
                Create {cfg.label}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" style={{ zIndex: 1085 }}></div>
    </>,
    document.body
  );
};
